import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Save, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';
import { ShippingInfoCard, EMPTY_SHIPPING, cleanShipping } from '../components/ShippingInfoCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CreateDeliveryOrder = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    po_number: '',
    delivery_address: '',
    pickup_address: '',
    delivery_date: '',
    delivery_time: '',
    pickup_contact: '',
    site_contact: '',
    site_phone: '',
    notes: ''
  });
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, hours: 0, unit_price: 0, amount: 0 }
  ]);
  const [shipping, setShipping] = useState(EMPTY_SHIPPING);

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, hours: 0, unit_price: 0, amount: 0 }]);
  };

  const removeLineItem = (index) => {
    const updated = lineItems.filter((_, i) => i !== index);
    setLineItems(updated);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price' || field === 'hours') {
      const qty = parseFloat(updated[index].quantity || 0);
      const hours = parseFloat(updated[index].hours || 0);
      const price = parseFloat(updated[index].unit_price || 0);
      updated[index].amount = hours > 0 ? qty * hours * price : qty * price;
    }
    
    setLineItems(updated);
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const doData = {
        ...formData,
        shipping_info: cleanShipping(shipping),
        line_items: lineItems.map(item => ({
          description: item.description,
          lorry_no: item.lorry_no || '',
          quantity: parseFloat(item.quantity),
          hours: parseFloat(item.hours) || 0,
          unit_price: parseFloat(item.unit_price),
          amount: parseFloat(item.amount)
        }))
      };

      const response = await axios.post(`${BACKEND_URL}/api/delivery-orders/`, doData);

      if (response.data.success) {
        toast.success(response.data.message);
        navigate('/admin/billing');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create delivery order'));
    } finally {
      setLoading(false);
    }
  };

  const subtotal = calculateSubtotal();

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/billing')}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create Delivery Order</h1>
              <p className="text-gray-600 mt-1">Generate a new delivery order</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Customer Name *</Label>
                      <Input
                        value={formData.customer_name}
                        onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        required
                        data-testid="customer-name-input"
                      />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input
                        type="tel"
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                        required
                        data-testid="customer-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                      required
                      data-testid="customer-email-input"
                    />
                  </div>
                  <div>
                    <Label>Customer P/O Number</Label>
                    <Input
                      value={formData.po_number}
                      onChange={(e) => setFormData({...formData, po_number: e.target.value})}
                      placeholder="Purchase Order number (optional)"
                      data-testid="po-number-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Delivery Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Pickup Address</Label>
                    <Textarea
                      value={formData.pickup_address}
                      onChange={(e) => setFormData({...formData, pickup_address: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Pickup Contact</Label>
                    <Input
                      value={formData.pickup_contact}
                      onChange={(e) => setFormData({...formData, pickup_contact: e.target.value})}
                      placeholder="e.g., Mr Lim 9123 4567"
                      data-testid="pickup-contact-input"
                    />
                  </div>
                  <div>
                    <Label>Delivery Address *</Label>
                    <Textarea
                      value={formData.delivery_address}
                      onChange={(e) => setFormData({...formData, delivery_address: e.target.value})}
                      rows={2}
                      required
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Site Contact Name</Label>
                      <Input
                        value={formData.site_contact}
                        onChange={(e) => setFormData({...formData, site_contact: e.target.value})}
                        placeholder="e.g., Mr Tan"
                        data-testid="site-contact-input"
                      />
                    </div>
                    <div>
                      <Label>Site Contact Phone</Label>
                      <Input
                        value={formData.site_phone}
                        onChange={(e) => setFormData({...formData, site_phone: e.target.value})}
                        placeholder="e.g., 9123 4567"
                        data-testid="site-phone-input"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Delivery Date *</Label>
                      <Input
                        type="date"
                        value={formData.delivery_date}
                        onChange={(e) => setFormData({...formData, delivery_date: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Delivery Time *</Label>
                      <Input
                        type="time"
                        value={formData.delivery_time}
                        onChange={(e) => setFormData({...formData, delivery_time: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Items to Deliver</CardTitle>
                    <Button type="button" onClick={addLineItem} variant="outline">
                      <Plus size={16} className="mr-2" />
                      Add Item
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {lineItems.map((item, index) => (
                      <div key={index} className="border p-4 rounded-lg space-y-4">
                        <div className="flex justify-between items-start">
                          <p className="font-semibold">Item {index + 1}</p>
                          {lineItems.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeLineItem(index)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                        <div>
                          <Label>Description *</Label>
                          <Input
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            required
                            placeholder="Supply of 25-ton lorry crane / trailer / tailgate / manpower..."
                            data-testid={`line-item-description-${index}`}
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Hours</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={item.hours || ''}
                              onChange={(e) => updateLineItem(index, 'hours', e.target.value)}
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <Label>Lorry No.</Label>
                            <Input
                              value={item.lorry_no || ''}
                              onChange={(e) => updateLineItem(index, 'lorry_no', e.target.value)}
                              placeholder="e.g. XD1234A"
                              data-testid={`do-line-item-lorry-${index}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <ShippingInfoCard value={shipping} onChange={setShipping} />

              <Card>
                <CardHeader>
                  <CardTitle>Additional Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={4}
                    placeholder="Special instructions, handling requirements, etc..."
                  />
                </CardContent>
              </Card>
            </div>

            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Items:</span>
                      <span className="font-semibold">{lineItems.length}</span>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <Button
                      type="submit"
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      disabled={loading}
                      data-testid="create-do-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Creating...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save size={20} />
                          Create Delivery Order
                        </span>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};